// Legal texts. Format: "## " heading, "- " bullet, "1. " numbered item, "| a | b |" table row (first row = header),
// **bold**, blank line between blocks.
// When either text changes, bump its version in LEGAL_VERSIONS (backend/server.py) so everyone accepts again.

export const LEGAL_NOTICE = `En Pensive sabemos que lo que escribes en tu diario es de lo más íntimo que tienes. Este aviso explica qué datos tratamos, para qué, con quién los compartimos y cómo puedes controlarlos.

Última actualización: 24 de octubre de 2026.

## 1. Quién es responsable de tus datos

[RAZÓN SOCIAL] ("Pensive"), con domicilio en [DOMICILIO], San Pedro Garza García, Nuevo León, México, es responsable del tratamiento de tus datos personales cuando usas la aplicación Pensive y el sitio pensive.lat.

Para cualquier tema de privacidad puedes escribir a [CORREO DE PRIVACIDAD] (por ejemplo, privacidad@pensive.lat).

## 2. Qué datos tratamos

| Tipo | Datos | ¿Sensibles? |
| Cuenta | Nombre, correo electrónico, contraseña (guardada cifrada, nunca en texto legible) o, si entras con Google, tu nombre y correo de Google | No |
| Lo que escribes | Entradas del diario, títulos, rutas de escritura elegidas, intervenciones de Remanso, emociones que marcas, tu respuesta a "¿Cómo te deja haber escrito hoy?" | Sí |
| Hábitos | Hábitos que registras, tus respuestas al cuestionario y los días que marcas | Sí |
| Preferencias | Recordatorios, hora y zona horaria, sonido, tema Día/Noche | No |
| Uso y seguridad | Fecha y hora de acceso, dirección IP, tipo de navegador, intentos fallidos de inicio de sesión, registros técnicos de errores (sin el contenido de lo que escribes) | No |

Lo que escribes y tus hábitos pueden revelar tu estado emocional o de salud. Por eso los tratamos como datos personales sensibles y solo los usamos con tu consentimiento expreso (sección 4).

No pedimos datos financieros, de pago ni de identificación oficial.

## 3. Para qué usamos tus datos

**Finalidades necesarias** (sin ellas no podemos darte el servicio):

1. Crear y administrar tu cuenta, y permitirte iniciar sesión.
2. Guardar tus entradas, emociones y hábitos para que solo tú puedas verlos.
3. Que Remanso, el acompañante de IA, lea tu entrada cuando se lo pides y te haga preguntas (sección 5).
4. Revisar el tono de lo que escribes para la regla de los 15 minutos y para detectar señales de riesgo y mostrarte el aviso de la Línea de la Vida (sección 5).
5. Redactar tu hábito en primera persona a partir de tus respuestas.
6. Enviarte los recordatorios que tú actives y los correos necesarios de tu cuenta (restablecer contraseña, códigos de seguridad).
7. Proteger tu cuenta: evitar accesos indebidos, abuso del servicio y fallas técnicas.
8. Cumplir obligaciones legales y atender requerimientos de autoridades.

**Finalidades adicionales** (puedes negarte y seguir usando Pensive):

- Enviarte novedades de Pensive y del blog por correo.
- Hacer estadísticas de uso agregadas y anónimas (por ejemplo, cuántas entradas se escriben al día), nunca con el contenido de lo que escribes.

Para negarte a las finalidades adicionales, desactívalas en Mi cuenta o escríbenos a [CORREO DE PRIVACIDAD] con el asunto "No a finalidades adicionales".

**Lo que nunca hacemos:** vender tus datos, usar lo que escribes para publicidad, ni usar tus entradas para entrenar modelos de inteligencia artificial.

## 4. Tu consentimiento para datos sensibles

La ley exige que nos des tu consentimiento expreso y por escrito para tratar datos sensibles. Lo das al crear tu cuenta, marcando una casilla que no viene marcada de antemano y que dice:

"Autorizo a Pensive a tratar lo que escriba en mi diario, las emociones que marque y mis hábitos, que pueden revelar información sobre mi salud emocional, para las finalidades necesarias de este aviso de privacidad."

Tu inicio de sesión (correo y contraseña, o Google) funciona como mecanismo de autenticación de ese consentimiento. Guardamos la fecha y la versión del aviso que aceptaste.

Sin este consentimiento no podemos guardar tu diario. Puedes revocarlo en cualquier momento (sección 8); al hacerlo, borramos tu cuenta y todo lo que escribiste.

## 5. Cómo usamos la inteligencia artificial

Remanso funciona con Claude, un modelo de inteligencia artificial de Anthropic. La IA de Pensive acompaña, no aconseja: no da consejos, opiniones ni diagnósticos.

**Qué se envía a la IA y cuándo:**

- Cuando tocas "Acompáñame" o "Cerrar la entrada": el texto de esa entrada, las emociones que marcaste, la ruta elegida y las últimas preguntas de Remanso.
- Mientras escribes: cada pocos minutos, el final de tu entrada, para clasificar su tono (difícil, mixto, neutro o positivo) y detectar señales de riesgo. El resultado solo se usa para que Remanso se asome después de 15 minutos escribiendo desde lo difícil, o para mostrarte el aviso de la Línea de la Vida.
- Al registrar un hábito: tus respuestas al cuestionario, para redactarlo en primera persona.

**Lo que no hace la IA:** no decide nada sobre ti, no crea perfiles, no te etiqueta con emociones ni condiciones, y no guarda lo que escribes para entrenarse. Anthropic procesa estos datos por encargo de Pensive y no los usa para entrenar sus modelos.

**Detección de riesgo:** si lo que escribes sugiere que podrías hacerte daño o que alguien está en peligro, Pensive solo te muestra en pantalla la Línea de la Vida (800 911 2000) y el 911. No avisamos a nadie ni guardamos esa clasificación en tu perfil.

Pensive no sustituye la atención de un profesional de la salud mental.

## 6. Con quién compartimos tus datos

No vendemos ni cedemos tus datos a terceros. Para que Pensive funcione, algunos proveedores los procesan por nuestra cuenta y bajo nuestras instrucciones (encargados). Están obligados por contrato a usarlos solo para darnos el servicio y a protegerlos.

| Proveedor | Para qué | Qué datos | Dónde |
| Emergent [confirmar razón social] | Alojamiento de la app, base de datos y conexión con la IA | Todos los de la sección 2, con el diario y los hábitos cifrados | [confirmar país de los servidores] |
| Anthropic, PBC (Claude) | Remanso: preguntas, cierre, análisis de tono, redacción de hábitos | Solo el texto necesario en cada llamada (sección 5) | Estados Unidos |
| Resend, Inc. | Envío de correos (recordatorios, contraseña, códigos) | Correo electrónico y el texto genérico del correo; nunca lo que escribes | Estados Unidos |
| Google LLC | Inicio de sesión con Google, si lo eliges | Nombre y correo de tu cuenta de Google | Estados Unidos |
| Servicio de notificaciones de tu navegador (Apple, Google o Mozilla) | Recordatorios por notificación, si los activas | Un identificador del dispositivo y el texto genérico del aviso | Según tu navegador |

Como algunos proveedores están fuera de México, tus datos pueden procesarse en el extranjero, con las mismas protecciones de este aviso.

**Transferencias que sí requerirán tu consentimiento:** solo compartiremos tus datos con un tercero si la ley lo exige o una autoridad lo ordena, o si en el futuro decides compartir entradas con tu psicoterapeuta. Esa función, cuando exista, será opcional, por entrada y revocable, y te pediremos tu autorización en ese momento.

[Pendiente: confirmar con Emergent la razón social, el país de sus servidores y su política sobre el texto que pasa por su conexión con la IA, y firmar con cada proveedor el acuerdo de encargado.]

## 7. Cómo protegemos y cuánto tiempo guardamos tus datos

**Seguridad:**

- Tus entradas, emociones, intervenciones de Remanso y hábitos se guardan cifrados en nuestra base de datos. Nadie del equipo de Pensive puede leerlos desde un panel.
- La conexión con la app va cifrada (HTTPS).
- Las contraseñas se guardan transformadas, nunca en texto legible.
- Protegemos el inicio de sesión con esperas crecientes, límites por conexión y un código por correo ante intentos sospechosos.
- Los registros de errores nunca incluyen lo que escribes.

**Conservación:**

- Guardamos tu diario y tus hábitos mientras tengas tu cuenta activa.
- Si borras una entrada o un hábito, se elimina de inmediato de la app y de nuestras copias de respaldo en un plazo máximo de [30] días.
- Si borras tu cuenta, eliminamos todos tus datos en ese mismo plazo, salvo lo que la ley nos obligue a conservar.
- Los registros de seguridad (accesos, intentos fallidos) se guardan hasta [90] días.

Si ocurriera una vulneración de seguridad que afecte tus derechos, te avisaremos sin demora por correo y en la app, con lo que pasó y lo que puedes hacer.

## 8. Tus derechos y cómo ejercerlos

Tienes derecho a acceder a tus datos, rectificarlos, cancelarlos y oponerte a su uso (derechos ARCO), así como a revocar tu consentimiento y limitar el uso de tus datos.

**Desde la app, sin trámites:**

- Ver y corregir todo lo que escribiste: en tu diario y en Hábitos.
- Descargar una copia de tus entradas y hábitos: Mi cuenta → Exportar.
- Borrar una entrada o un hábito: desde cada uno.
- Borrar tu cuenta y todos tus datos: Mi cuenta → Borrar cuenta.
- Apagar recordatorios y correos de novedades: Mi cuenta.

**Por correo, para cualquier otra solicitud:** escribe a [CORREO DE PRIVACIDAD] desde el correo de tu cuenta, con tu nombre, el derecho que quieres ejercer y una descripción clara de lo que pides.

Te responderemos en un máximo de 20 días hábiles, y si procede, lo haremos efectivo en los 15 días hábiles siguientes. Ejercer estos derechos es gratuito.

Si consideras que no atendimos tu solicitud, puedes acudir a la Secretaría Anticorrupción y Buen Gobierno, la autoridad de protección de datos personales en México.

## 9. Cookies y tecnologías similares

Pensive usa solo las cookies y el almacenamiento del navegador necesarios para funcionar: mantener tu sesión iniciada, recordar tus preferencias (tema, sonido) y proteger tu cuenta. No usamos cookies de publicidad ni rastreadores de terceros dentro de la app.

[Si el sitio pensive.lat usa analítica o píxeles de redes sociales, describirlos aquí y pedir consentimiento antes de activarlos.]

## 10. Menores de edad, cambios y autoridad

**Menores de edad.** Pensive está dirigido a personas mayores de 18 años. No recabamos a sabiendas datos de menores. Si eres madre, padre o tutor y crees que un menor nos dio sus datos, escríbenos y los borraremos.

**Cambios a este aviso.** Si cambiamos este aviso, te avisaremos en la app y por correo antes de que el cambio aplique. Si el cambio afecta el uso de tus datos sensibles, te pediremos de nuevo tu consentimiento.

**Autoridad.** Este aviso se rige por la Ley Federal de Protección de Datos Personales en Posesión de los Particulares y su reglamento. La autoridad competente es la Secretaría Anticorrupción y Buen Gobierno.

## Aviso de privacidad simplificado

[RAZÓN SOCIAL] ("Pensive") trata tu nombre, correo y lo que escribes en tu diario, tus emociones y tus hábitos — que pueden revelar información sobre tu salud emocional — para darte el servicio de diario con acompañamiento de IA, guardar tu información de forma privada y proteger tu cuenta. No vendemos tus datos ni los usamos para publicidad o para entrenar modelos de IA. Puedes negarte a recibir novedades desde Mi cuenta. Consulta el aviso de privacidad integral en pensive.lat/privacidad.`;

export const TERMINOS = `Estos términos son el acuerdo entre tú y [RAZÓN SOCIAL] ("Pensive", "nosotros") para usar la aplicación Pensive y el sitio pensive.lat. Al crear tu cuenta aceptas estos términos y el Aviso de privacidad. Si no estás de acuerdo, no uses Pensive.

Última actualización: 24 de octubre de 2026.

## 1. Qué es Pensive y qué no es

Pensive es un diario personal con un acompañante de inteligencia artificial llamado Remanso, una guía de emociones y una sección de hábitos. Remanso acompaña, no aconseja: lee lo que escribes cuando se lo pides y te hace preguntas para que sigas escribiendo.

**Pensive no es:**

- **Terapia ni atención de salud.** No diagnostica, no trata ni sustituye a un psicólogo, psiquiatra o médico.
- **Un servicio de emergencias.** No monitoreamos lo que escribes en tiempo real con personas ni avisamos a nadie. Si estás en peligro, llama al 911. Si piensas en hacerte daño, la Línea de la Vida (800 911 2000) atiende gratis las 24 horas.
- **Una fuente de consejos.** Remanso no te dice qué hacer. Las decisiones que tomes son tuyas.

La guía de emociones es informativa y general; cada persona siente distinto.

## 2. Tu cuenta

- Debes tener al menos 18 años para usar Pensive.
- Usa un correo que sea tuyo y datos verdaderos. Una cuenta es personal: no la compartas.
- Cuida tu contraseña. Eres responsable de lo que se haga desde tu cuenta, salvo que nos avises de un acceso indebido.
- Si crees que alguien entró a tu cuenta, cambia tu contraseña con "Olvidé mi contraseña" y escríbenos a [CORREO DE CONTACTO].
- Lo que escribes está en tu cuenta. Si alguien tiene acceso a tu dispositivo con la sesión abierta, puede leerlo: cierra sesión en dispositivos compartidos.

## 3. Lo que escribes es tuyo

Todo lo que escribes en Pensive —entradas, hábitos, emociones que marcas— es tuyo. No adquirimos ningún derecho de propiedad sobre ello.

Para que el servicio funcione, nos das un permiso limitado, no exclusivo y gratuito para guardar, cifrar, respaldar y procesar tu contenido solo para:

- Mostrártelo a ti.
- Que Remanso pueda leerlo cuando lo pides o para detectar el tono y señales de riesgo, como explica el Aviso de privacidad.
- Generar la copia que descargas con Exportar.

Ese permiso termina cuando borras el contenido o tu cuenta. No usamos tu contenido para publicidad, no lo publicamos, no lo vendemos y no lo usamos para entrenar modelos de inteligencia artificial.

Puedes descargar tus datos en cualquier momento desde Mi cuenta → Exportar.

## 4. Uso aceptable

Pensive es para tu escritura personal. No puedes:

- Intentar entrar a cuentas ajenas, saltarte los límites de seguridad o interferir con el funcionamiento del servicio.
- Usar programas automáticos para enviar textos a Remanso, extraer información o hacer llamadas masivas.
- Intentar que Remanso genere contenido ilegal o dañino, o manipularlo para que actúe fuera de su propósito.
- Copiar, revender o redistribuir el servicio.

Para proteger el servicio, el uso de Remanso tiene un límite razonable de solicitudes por hora. Si lo alcanzas, verás un aviso y podrás seguir escribiendo; Remanso vuelve a estar disponible después.

**Sobre Remanso:** sus preguntas las genera una inteligencia artificial. Puede equivocarse, no entender el contexto o hacer una pregunta que no te sirva. Tú decides si la respondes o la ignoras.

## 5. Lo que es de Pensive

El nombre Pensive, el logotipo, los personajes Remansos y su historia, las ilustraciones, el diseño de la app, las rutas de escritura, los textos de la guía de emociones y el software son propiedad de [RAZÓN SOCIAL] o se usan con licencia. Puedes usarlos solo dentro de la app, para tu uso personal. No puedes copiarlos, modificarlos ni usarlos con fines comerciales sin nuestra autorización por escrito.

Si nos mandas sugerencias o comentarios sobre Pensive, podemos usarlos para mejorar el servicio sin ninguna obligación contigo. Esto no aplica a lo que escribes en tu diario.

## 6. Precio, cambios y disponibilidad

**Precio.** Por ahora Pensive es gratuito. Si en el futuro ofrecemos planes de pago, te avisaremos con al menos [30] días de anticipación, con el precio en pesos mexicanos e impuestos incluidos, y nunca te cobraremos sin que lo aceptes expresamente. [Cuando haya cobros, agregar condiciones de pago, renovación, cancelación y reembolso conforme a la Ley Federal de Protección al Consumidor.]

**Cambios en el servicio.** Podemos agregar, cambiar o quitar funciones. Si quitamos algo importante, te avisaremos antes y siempre podrás descargar tus datos.

**Disponibilidad.** Trabajamos para que Pensive esté disponible siempre, pero puede haber interrupciones por mantenimiento, fallas de proveedores o causas fuera de nuestro control. Los recordatorios y las respuestas de Remanso pueden retrasarse o no llegar. Te recomendamos exportar tus datos de vez en cuando.

## 7. Limitación de responsabilidad y situaciones de riesgo

**Pensive no es terapia.** Remanso es una inteligencia artificial que acompaña tu escritura con preguntas. No da consejos, diagnósticos ni tratamientos, y no sustituye la atención de un profesional de la salud mental.

**Remanso puede equivocarse.** Sus preguntas pueden no ser las adecuadas para tu momento. Lo que decidas hacer a partir de lo que escribes es tu decisión, y Pensive no responde por esas decisiones.

**Si estás en riesgo.** Si en algún momento piensas en hacerte daño o en quitarte la vida, o alguien está en peligro, llama al 911 o a la Línea de la Vida (800 911 2000, gratuita, las 24 horas). Pensive puede mostrarte estos datos cuando detecta palabras de riesgo, pero esa detección no es perfecta: puede no activarse aunque lo necesites. Nadie del equipo lee tus entradas ni puede responder a una emergencia.

**Hasta dónde respondemos.** En la medida en que la ley lo permite, Pensive no es responsable por daños indirectos, pérdida de información causada por fallas de terceros ni por el uso que hagas del servicio contrario a estos términos. Nada de esto limita los derechos que te da la Ley Federal de Protección al Consumidor ni nuestra responsabilidad por dolo o negligencia grave.

## 8. Terminación, cambios, ley aplicable y contacto

**Si te vas.** Puedes borrar tu cuenta cuando quieras desde Mi cuenta. Se borran tu cuenta, tus entradas, tus hábitos y sus registros; las copias de respaldo se eliminan en un máximo de [30] días. Antes puedes exportar todo.

**Si suspendemos una cuenta.** Solo lo haremos por un uso contrario a estos términos (por ejemplo, ataques al servicio o uso automatizado abusivo). Salvo que haya un riesgo inmediato, te avisaremos antes por correo y te daremos oportunidad de explicar y de descargar tus datos.

**Cambios a estos términos.** Si cambian, te avisaremos dentro de la app y por correo al menos [15] días antes de que entren en vigor. Si no estás de acuerdo, puedes dejar de usar Pensive y borrar tu cuenta.

**Ley aplicable.** Estos términos se rigen por las leyes federales de México. Para cualquier controversia, las partes se someten a los tribunales de Monterrey, Nuevo León, sin perjuicio de que puedas acudir a la Procuraduría Federal del Consumidor (Profeco).

**Contacto.** [RAZÓN SOCIAL], [DOMICILIO]. Correo: [CORREO DE CONTACTO].`;
